import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus, Save, Trash2, Package, Shield, Percent } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDiscountCaps = ({ user }) => {
  const navigate = useNavigate();
  const [caps, setCaps] = useState([]);
  const [globalCaps, setGlobalCaps] = useState({ tier1: 15, tier2: 10 });
  const [editingGlobalCaps, setEditingGlobalCaps] = useState({ tier1: 15, tier2: 10 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newProduct, setNewProduct] = useState({ sku: "", description: "", reason: "" });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchCaps = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/discount-caps`, { headers: getAuthHeaders() });
      setCaps(response.data.caps || []);
      
      // Get global caps from response or use defaults
      const globalTier1 = response.data.global_tier1_cap || 15;
      const globalTier2 = response.data.global_tier2_cap || 10;
      setGlobalCaps({ tier1: globalTier1, tier2: globalTier2 });
      setEditingGlobalCaps({ tier1: globalTier1, tier2: globalTier2 });
      
      setError("");
    } catch (err) {
      setError("Failed to fetch discount caps");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'zkteco_admin' || user.role === 'zkteco')) {
      fetchCaps();
    } else {
      setError("Admin access required");
      setLoading(false);
    }
  }, [user]);

  const handleSaveGlobalCaps = async () => {
    try {
      setError("");
      setSuccess("");
      
      await axios.put(
        `${API}/admin/discount-caps/global`,
        {
          tier1_cap: editingGlobalCaps.tier1,
          tier2_cap: editingGlobalCaps.tier2
        },
        { headers: getAuthHeaders() }
      );
      
      setGlobalCaps({ ...editingGlobalCaps });
      setSuccess("Global discount caps updated successfully");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update global caps");
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    if (!newProduct.sku.trim()) {
      setError("SKU is required");
      return;
    }
    
    try {
      setError("");
      setSuccess("");
      
      await axios.post(
        `${API}/admin/discount-caps`,
        {
          sku: newProduct.sku.trim(),
          description: newProduct.description.trim(),
          reason: newProduct.reason.trim()
        },
        { headers: getAuthHeaders() }
      );
      
      setSuccess(`Product ${newProduct.sku} added to capped list`);
      setNewProduct({ sku: "", description: "", reason: "" });
      setShowAddForm(false);
      fetchCaps();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add product");
    }
  };

  const handleRemoveProduct = async (sku) => {
    if (!window.confirm(`Remove ${sku} from capped products list?`)) return;
    
    try {
      setError("");
      setSuccess("");
      
      await axios.delete(`${API}/admin/discount-caps/${sku}`, { headers: getAuthHeaders() });
      
      setSuccess(`Product ${sku} removed from capped list`);
      fetchCaps();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to remove product");
    }
  };

  if (!user || (user.role !== 'admin' && user.role !== 'zkteco_admin' && user.role !== 'zkteco')) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Alert variant="destructive">
          <AlertDescription>Admin access required</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Product Discount Caps</h1>
            <p className="text-slate-500 text-sm">Manage maximum discounts for specific products</p>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="mb-4 bg-green-50 border-green-200">
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {/* Global Tier Caps Card */}
        <Card className="mb-6 border-orange-200">
          <CardHeader className="bg-orange-50">
            <CardTitle className="flex items-center gap-2 text-orange-800">
              <Percent className="h-5 w-5" />
              Global Tier Caps
            </CardTitle>
            <CardDescription>
              These caps apply to ALL products in the capped list below
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="tier1">Tier 1 Cap (Main Distributors)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="tier1"
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={editingGlobalCaps.tier1}
                    onChange={(e) => setEditingGlobalCaps(prev => ({ ...prev, tier1: parseFloat(e.target.value) || 0 }))}
                    className="w-24"
                  />
                  <span className="text-slate-500">%</span>
                </div>
                <p className="text-xs text-slate-500">Maximum discount for Tier 1 distributors on capped products</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="tier2">Tier 2 Cap (Sub-Distributors)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="tier2"
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={editingGlobalCaps.tier2}
                    onChange={(e) => setEditingGlobalCaps(prev => ({ ...prev, tier2: parseFloat(e.target.value) || 0 }))}
                    className="w-24"
                  />
                  <span className="text-slate-500">%</span>
                </div>
                <p className="text-xs text-slate-500">Maximum discount for Tier 2 distributors on capped products</p>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <Button
                onClick={handleSaveGlobalCaps}
                disabled={editingGlobalCaps.tier1 === globalCaps.tier1 && editingGlobalCaps.tier2 === globalCaps.tier2}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Global Caps
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Capped Products List Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-slate-600" />
                  Capped Products
                </CardTitle>
                <CardDescription>
                  Products in this list will have the global tier caps applied
                </CardDescription>
              </div>
              <Button
                onClick={() => setShowAddForm(!showAddForm)}
                className="bg-green-600 hover:bg-green-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Add Product Form */}
            {showAddForm && (
              <form onSubmit={handleAddProduct} className="mb-6 p-4 bg-slate-50 rounded-lg border">
                <h3 className="font-semibold mb-4">Add Product to Capped List</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-sku">SKU *</Label>
                    <Input
                      id="new-sku"
                      value={newProduct.sku}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, sku: e.target.value }))}
                      placeholder="e.g., ACC-BASE-COMET-B-UK"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-desc">Description</Label>
                    <Input
                      id="new-desc"
                      value={newProduct.description}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Product description"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-reason">Reason</Label>
                    <Input
                      id="new-reason"
                      value={newProduct.reason}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, reason: e.target.value }))}
                      placeholder="e.g., Low margin item"
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button type="submit" className="bg-green-600 hover:bg-green-700">
                    Add Product
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {/* Products Table */}
            {caps.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Package className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>No products in the capped list</p>
                <p className="text-sm">Add products that should have limited discount caps</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">SKU</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">Description</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-600">Reason</th>
                      <th className="text-center py-3 px-4 font-semibold text-slate-600">Applied Caps</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {caps.map((cap) => (
                      <tr key={cap.sku} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 font-mono text-sm">{cap.sku}</td>
                        <td className="py-3 px-4 text-slate-600">{cap.description || '-'}</td>
                        <td className="py-3 px-4 text-slate-500 text-xs">{cap.reason || '-'}</td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex justify-center gap-2">
                            <Badge className={`${cap.tier1_cap === globalCaps.tier1 ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`}>
                              T1: {cap.tier1_cap != null ? cap.tier1_cap : globalCaps.tier1}%
                            </Badge>
                            <Badge className={`${cap.tier2_cap === globalCaps.tier2 ? 'bg-purple-100 text-purple-800' : 'bg-red-100 text-red-800'}`}>
                              T2: {cap.tier2_cap != null ? cap.tier2_cap : globalCaps.tier2}%
                            </Badge>
                          </div>
                          {(cap.tier1_cap != null && cap.tier1_cap !== globalCaps.tier1) || (cap.tier2_cap != null && cap.tier2_cap !== globalCaps.tier2) ? (
                            <p className="text-xs text-red-600 mt-1">Out of sync with global caps!</p>
                          ) : null}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveProduct(cap.sku)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <h3 className="font-semibold text-blue-900 mb-2">💡 How Product Caps Work</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Products in this list have a <strong>fixed discount rate</strong> regardless of the distributor's overall discount tier</li>
              <li>• <strong>Tier 1</strong> applies to main distributors (Pricing Tier: Main Channel)</li>
              <li>• <strong>Tier 2</strong> applies to sub-distributors (Pricing Tier: Sub Distribution)</li>
              <li>• Example: If a distributor has 25% discount but Tier 1 cap is 15%, they only get 15% on these products</li>
              <li>• Caps shown in <span className="text-red-600 font-bold">red</span> are out of sync with global caps - save global caps to fix</li>
              <li>• All other products (not in this list) receive the full distributor discount</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProductDiscountCaps;
